import { HistoricalUtrs } from '@g17eco/types/insight-custom-dashboard';
import { UtrvType } from '@constants/status';
import { isNumericString } from '@utils/string';
import { UniversalTrackerModalServiceUtrv, UtrvInitiativeRef } from '@g17eco/types/universalTrackerValue';
import { TableColumn, TableColumnType } from '@g17eco/types/universalTracker';
import { loggerMessage } from '../../../../logger';

export const isNumericColumn = (type: TableColumnType) =>
  type === TableColumnType.Number || type === TableColumnType.Percentage;

export const findValueListColumn = (
  utr: HistoricalUtrs['utr'] | undefined,
  valueListCode: string | undefined,
): TableColumn | undefined => {
  if (!utr || !valueListCode) {
    return undefined;
  }
  return utr.valueValidation?.table?.columns?.find((c) => c.code === valueListCode);
};

export type ResolvedColumn = {
  column: TableColumn | undefined;
  // Kept on the result so cell evaluators can reach the raw text value via getMetricText
  // without re-threading valueListCode through every call site.
  valueListCode: string | undefined;
  unit: string;
};

const resolveColumnUnit = (
  utr: HistoricalUtrs['utr'] | undefined,
  column: TableColumn | undefined,
): string => {
  if (!column) {
    return utr?.unit ?? '';
  }
  if (!isNumericColumn(column.type)) {
    return '';
  }
  return column.unit ?? '';
};

export const resolveTableColumn = (
  utr: HistoricalUtrs['utr'] | undefined,
  valueListCode: string | undefined,
): ResolvedColumn => {
  const column = findValueListColumn(utr, valueListCode);
  return { column, valueListCode, unit: resolveColumnUnit(utr, column) };
};

// Transposed mode shows periods on rows; allow a full year or two before truncating.
export const NUMBER_OF_ROWS_TRANSPOSED = 24;

export const getActualUtrsData = (utrsData: HistoricalUtrs[]): HistoricalUtrs[] => {
  return utrsData.map((utrData) => {
    return { ...utrData, utrvs: utrData.utrvs.filter((utrv) => utrv.type === UtrvType.Actual) };
  });
};

export const getDisplayNumericValue = (calculationValue: string | number | null | undefined) => {
  return isNumericString(calculationValue)
    ? calculationValue.toLocaleString('en-GB', {
        maximumFractionDigits: 2,
      })
    : calculationValue;
};

export const collectUtrvInitiatives = (utrvs: UniversalTrackerModalServiceUtrv[]): UtrvInitiativeRef[] => {
  const map = new Map<string, UtrvInitiativeRef>();
  const untaggedIds: string[] = [];
  utrvs.forEach((utrv) => {
    const { initiative } = utrv;
    if (initiative?._id) {
      if (!map.has(initiative._id)) {
        map.set(initiative._id, initiative);
      }
    } else {
      untaggedIds.push(utrv._id);
    }
  });
  // Only log when the data is mixed (some tagged, some not) — that's a real invariant
  // violation. All-untagged is normal in single-subsidiary mode where the API doesn't tag
  // utrvs at all, and TableWidget calls this on every render to detect disaggregation.
  if (map.size > 0 && untaggedIds.length > 0) {
    loggerMessage('Disaggregated table: utrvs missing initiative tag', {
      level: 'error',
      untaggedUtrvIds: untaggedIds,
      untaggedCount: untaggedIds.length,
    });
  }
  return Array.from(map.values());
};

export const collectTableInitiatives = (utrsData: HistoricalUtrs[]): UtrvInitiativeRef[] =>
  collectUtrvInitiatives(utrsData.flatMap((data) => data.utrvs));

export const sliceUtrsDataByInitiative = (utrsData: HistoricalUtrs[], initiativeId: string): HistoricalUtrs[] =>
  utrsData.map((data) => ({
    ...data,
    utrvs: data.utrvs.filter((utrv) => utrv.initiative?._id === initiativeId),
  }));

export const disaggregatedCellKey = (initiativeId: string, effectiveDate: string) => `${initiativeId}|${effectiveDate}`;

/**
 * Pre-index utrvs by `${initiativeId}|${effectiveDate}` for O(1) cell lookup in disaggregated
 * tables — callers can avoid `Array.find` over every utrv per cell.
 *
 * `initiativeId` is schema-required on every utrv and the dashboard service always attaches
 * `initiative` before the FE sees the data, so a drop here means an upstream invariant was
 * violated. We log it rather than swallow silently.
 */
export const indexUtrvsByInitiativeAndDate = (
  utrvs: UniversalTrackerModalServiceUtrv[] | undefined,
): Map<string, UniversalTrackerModalServiceUtrv> => {
  const index = new Map<string, UniversalTrackerModalServiceUtrv>();
  const droppedIds: string[] = [];
  utrvs?.forEach((utrv) => {
    if (utrv.initiative?._id && utrv.effectiveDate) {
      index.set(disaggregatedCellKey(utrv.initiative._id, utrv.effectiveDate), utrv);
    } else {
      droppedIds.push(utrv._id);
    }
  });
  if (droppedIds.length) {
    loggerMessage('Disaggregated table: dropped utrvs missing initiative._id or effectiveDate', {
      level: 'error',
      droppedUtrvIds: droppedIds,
      droppedCount: droppedIds.length,
    });
  }
  return index;
};
